// IndexedDB layer for offline question caching and queued submissions.

import { openDB, type IDBPDatabase } from 'idb'

export interface Option { key: string; text: string }

export interface CachedQuestion {
  id: string
  body: string
  options: Option[]
  difficulty: number
  source_institution: string
  source_exam_year: number | null
  source_paper: string | null
  question_number: number | null
  generation_source?: string
  subject: { code: string; name: string } | null
  topic: { id?: string; code: string; name: string } | null
  page_number: number | null
  figure_url: string | null
  // When present, the passage should be rendered with the question.
  passage?: { title: string | null; author: string | null; body: string } | null
  passage_id?: string | null
  passage_section_index?: number | null
  requires_passage?: boolean
  // Included for offline answer evaluation.
  correct_answer: string
  hint: string | null
  resolution: string | null
  // Approved per-option explanations used for offline teaching moments.
  wrong_answer_explanations?: Array<{ option_key: string; explanation: string }>
  // Deprecated compatibility field.
  wrong_explanation?: string | null
}

export interface QueuedAttempt {
  id: string          // client uuid — crypto.randomUUID()
  questionId: string
  subjectCode: string
  selected: string
  timeSpentMs: number
  createdAt: number   // Date.now()
}

export interface QueuedExamAnswer {
  id: string          // `${sessionId}:${questionId}`
  sessionId: string
  questionId: string
  selected: string
  queuedAt: number
}

export interface PackMeta {
  id: 'current'           // singleton key — only one pack at a time
  programmeNames: string[]
  subjects: { code: string; name: string; count: number }[]
  totalQuestions: number
  downloadedAt: string    // ISO timestamp
  creditsDeducted: number
}

export const OFFLINE_PACK_META_UPDATED_EVENT = 'tutor:offline-pack-meta-updated'
const PACK_META_STORAGE_KEY = 'tutor.offline.pack_meta'

export type CachedQuestionMode = 'admission' | 'national_12'
export type CachedQuestionOrigin = 'readiness_fill' | 'manual_refresh' | 'session_carry' | 'pack_download'
export type FreshnessStatus = 'fresh' | 'stale'

interface CacheWriteOptions {
  mode?: CachedQuestionMode | null
  origin?: CachedQuestionOrigin
  cachedAtBand?: number | null
  replaceExisting?: boolean
}

interface CacheReadOptions {
  mode?: CachedQuestionMode | null
  topicId?: string | null
  includeAttempted?: boolean
}

interface QuestionEntry {
  id: string          // = question.id — used as IDB key
  subjectCode: string // used as index
  subjectMode: string // used as index for mode-aware cache reads
  mode: CachedQuestionMode | null
  topicId: string | null
  sourceInstitution: string | null
  cachedAt: number
  cachedAtBand: number | null
  freshnessStatus: FreshnessStatus
  origin: CachedQuestionOrigin
  offlineAttemptedAt: number | null
  offlineWasCorrect: boolean | null
  question: CachedQuestion
}

const DB_NAME = 'acesso-v1'
const DB_VERSION = 5

let _db: IDBPDatabase | null = null

async function getDb(): Promise<IDBPDatabase> {
  if (!_db) {
    _db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, _newVersion, transaction) {
        if (oldVersion < 1) {
          const store = db.createObjectStore('question-cache', { keyPath: 'id' })
          store.createIndex('by-subject', 'subjectCode', { unique: false })
          db.createObjectStore('attempt-queue', { keyPath: 'id' })
        }
        if (oldVersion < 2) {
          if (!db.objectStoreNames.contains('pack-meta')) {
            db.createObjectStore('pack-meta', { keyPath: 'id' })
          }
        }
        if (oldVersion < 3) {
          const store = transaction.objectStore('question-cache')
          if (!store.indexNames.contains('by-subject-mode')) {
            store.createIndex('by-subject-mode', 'subjectMode', { unique: false })
          }
        }
        if (oldVersion < 4) {
          if (!db.objectStoreNames.contains('figure-cache')) {
            const store = db.createObjectStore('figure-cache', { keyPath: 'url' })
            store.createIndex('by-last-accessed', 'lastAccessedAt', { unique: false })
          }
        }
        if (oldVersion < 5) {
          if (!db.objectStoreNames.contains('exam-answer-queue')) {
            const store = db.createObjectStore('exam-answer-queue', { keyPath: 'id' })
            store.createIndex('by-session', 'sessionId', { unique: false })
            store.createIndex('by-queued-at', 'queuedAt', { unique: false })
          }
        }
      },
      terminated() {
        _db = null
      },
    })
  }
  return _db
}

// Retry once when IndexedDB closes underneath us during dev/HMR.
async function withDbRetry<T>(op: (db: IDBPDatabase) => Promise<T>): Promise<T> {
  try {
    return await op(await getDb())
  } catch (err: any) {
    const closing =
      err?.name === 'InvalidStateError' ||
      /database connection is closing/i.test(err?.message ?? '')
    if (!closing) throw err
    _db = null
    return op(await getDb())
  }
}

function readStoredPackMeta(): PackMeta | null {
  try {
    const raw = window.localStorage.getItem(PACK_META_STORAGE_KEY)
    return raw ? JSON.parse(raw) as PackMeta : null
  } catch {
    return null
  }
}

function writeStoredPackMeta(meta: PackMeta | null) {
  try {
    if (meta) {
      window.localStorage.setItem(PACK_META_STORAGE_KEY, JSON.stringify(meta))
    } else {
      window.localStorage.removeItem(PACK_META_STORAGE_KEY)
    }
  } catch {
    /* best-effort fallback */
  }
}

function buildQuestionEntry(
  subjectCode: string,
  question: CachedQuestion,
  opts: CacheWriteOptions = {},
): QuestionEntry {
  const mode = opts.mode ?? null
  return {
    id: question.id,
    subjectCode,
    subjectMode: `${subjectCode}:${mode ?? 'all'}`,
    mode,
    topicId: question.topic?.id ?? null,
    sourceInstitution: question.source_institution ?? null,
    cachedAt: Date.now(),
    cachedAtBand: opts.cachedAtBand ?? question.difficulty ?? null,
    freshnessStatus: 'fresh',
    origin: opts.origin ?? 'readiness_fill',
    offlineAttemptedAt: null,
    offlineWasCorrect: null,
    question,
  }
}

function entryMatchesReadOptions(entry: QuestionEntry, opts: CacheReadOptions = {}): boolean {
  if (!opts.includeAttempted && entry.offlineAttemptedAt) return false
  if (opts.mode && entry.mode && entry.mode !== opts.mode) return false
  if (opts.topicId && entry.topicId && entry.topicId !== opts.topicId) return false
  return true
}

function pickQuestionEntry(entries: QuestionEntry[]): QuestionEntry | null {
  if (entries.length === 0) return null
  const fresh = entries.filter((entry) => entry.freshnessStatus === 'fresh')
  const pool = (fresh.length > 0 ? fresh : entries)
    .sort((a, b) => b.cachedAt - a.cachedAt)
    .slice(0, 5)
  return pool[Math.floor(Math.random() * pool.length)] ?? null
}

export async function cacheQuestions(subjectCode: string, questions: CachedQuestion[], opts: CacheWriteOptions = {}) {
  const db = await getDb()
  const existing = opts.replaceExisting !== false
    ? (
        opts.mode
          ? (await db.getAllFromIndex('question-cache', 'by-subject-mode', `${subjectCode}:${opts.mode}`))
          : (await db.getAllFromIndex('question-cache', 'by-subject', subjectCode))
      ) as QuestionEntry[]
    : []
  const tx = db.transaction('question-cache', 'readwrite')
  if (existing.length > 0) {
    for (const e of existing) await tx.store.delete(e.id)
  }
  for (const q of questions) {
    await tx.store.put(buildQuestionEntry(subjectCode, q, opts))
  }
  await tx.done
}

/**
 * Pick a random cached question that hasn't been used this session.
 * Returns null when the cache is empty or all questions have been used.
 */
export async function getNextCachedQuestion(
  subjectCode: string,
  usedIds: Set<string>,
  opts: CacheReadOptions = {},
): Promise<CachedQuestion | null> {
  const db = await getDb()
  const all = opts.mode
    ? (await db.getAllFromIndex('question-cache', 'by-subject-mode', `${subjectCode}:${opts.mode}`)) as QuestionEntry[]
    : (await db.getAllFromIndex('question-cache', 'by-subject', subjectCode)) as QuestionEntry[]
  const available = all.filter((e) => !usedIds.has(e.question.id) && entryMatchesReadOptions(e, opts))
  if (!available.length) return null

  const topicMatched = opts.topicId
    ? available.filter((entry) => entry.topicId === opts.topicId)
    : available

  return pickQuestionEntry(topicMatched.length > 0 ? topicMatched : available)?.question ?? null
}

/** How many questions are cached for a subject right now. */
export async function countCachedQuestions(subjectCode: string, opts: CacheReadOptions = {}): Promise<number> {
  return withDbRetry(async (db) => {
    const all = opts.mode
      ? await db.getAllFromIndex('question-cache', 'by-subject-mode', `${subjectCode}:${opts.mode}`) as QuestionEntry[]
      : await db.getAllFromIndex('question-cache', 'by-subject', subjectCode) as QuestionEntry[]
    return all.filter((entry) => entryMatchesReadOptions(entry, opts)).length
  })
}

// ── Offline pack ─────────────────────────────────────────────────────────────

/** Save pack metadata (singleton — replaces any existing). */
export async function savePackMeta(meta: PackMeta) {
  writeStoredPackMeta(meta)
  try {
    const db = await getDb()
    await db.put('pack-meta', meta)
  } catch (err) {
    console.error('[offlineDb] savePackMeta failed, using localStorage fallback', err)
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(OFFLINE_PACK_META_UPDATED_EVENT))
  }
}

/** Get the current pack metadata, or null if no pack is downloaded. */
export async function getPackMeta(): Promise<PackMeta | null> {
  return withDbRetry(async db => {
    try {
      const result = await db.get('pack-meta', 'current')
      return (result as PackMeta) ?? readStoredPackMeta()
    } catch {
      return readStoredPackMeta()
    }
  })
}

/**
 * Drop cached questions for `mode` whose subject is no longer in the pack.
 *
 * Replaces the previous `clearPack()` call inside a background fill. clearPack
 * wipes the WHOLE question-cache store, which is fine for a single-mode fill
 * but destroys the other mode's questions the moment a student who can reach
 * both surfaces refreshes: mode B's clear would delete everything mode A had
 * just written.
 *
 * Scoped deliberately narrowly. `cacheQuestions(replaceExisting: true)`
 * already replaces each `subject:mode` bucket it touches, so the only gap this
 * closes is evicting subjects the student has dropped, which no longer appear
 * in any response and would otherwise linger forever.
 *
 * Returns the number of entries removed, for the smoke to assert against.
 */
export async function pruneCachedSubjectsForMode(
  keepSubjectCodes: string[],
  mode: CachedQuestionMode | null,
): Promise<number> {
  const keep = new Set(keepSubjectCodes)
  try {
    const db = await getDb()
    const all = (await db.getAll('question-cache')) as QuestionEntry[]
    const doomed = all.filter(e => e.mode === mode && !keep.has(e.subjectCode))
    if (doomed.length === 0) return 0
    const tx = db.transaction('question-cache', 'readwrite')
    for (const e of doomed) await tx.store.delete(e.id)
    await tx.done
    return doomed.length
  } catch (err) {
    console.error('[offlineDb] pruneCachedSubjectsForMode failed', err)
    return 0
  }
}

export async function pruneCachedModes(
  keepModes: CachedQuestionMode[],
): Promise<number> {
  const keep = new Set(keepModes)
  try {
    const db = await getDb()
    const all = (await db.getAll('question-cache')) as QuestionEntry[]
    const doomed = all.filter((entry) => !entry.mode || !keep.has(entry.mode))
    if (doomed.length === 0) return 0
    const tx = db.transaction('question-cache', 'readwrite')
    for (const entry of doomed) await tx.store.delete(entry.id)
    await tx.done
    return doomed.length
  } catch (err) {
    console.error('[offlineDb] pruneCachedModes failed', err)
    return 0
  }
}

/**
 * Split `total` across `modes` without wasting capacity.
 *
 * An even split starves nothing but wastes slots: the national_12 pool is a
 * fraction of the admission pool, so an even share would leave 12ª unable to
 * use its half while admission is trimmed below what it could hold. This
 * water-fills instead. Every mode gets an equal share, any mode holding less
 * than its share keeps everything it has, and the freed capacity is
 * redistributed among the modes that are still over. Repeats until nothing
 * more can be handed out.
 *
 * Exported for the smoke, which asserts the allocation directly rather than
 * inferring it from cache contents.
 */
export function allocatePackBudget(
  total: number,
  counts: Map<CachedQuestionMode | null, number>,
): Map<CachedQuestionMode | null, number> {
  const allowance = new Map<CachedQuestionMode | null, number>()
  let remaining = Math.max(0, total)
  let open = [...counts.keys()]

  while (open.length > 0 && remaining > 0) {
    const share = Math.floor(remaining / open.length)
    if (share <= 0) break
    const settled = open.filter(m => (counts.get(m) ?? 0) <= share)
    if (settled.length === 0) {
      // Everyone still open wants more than an equal share: give each the
      // share and stop. Distribute the rounding remainder deterministically.
      let extra = remaining - share * open.length
      for (const m of open) {
        allowance.set(m, share + (extra > 0 ? 1 : 0))
        if (extra > 0) extra--
      }
      remaining = 0
      open = []
      break
    }
    for (const m of settled) {
      const take = counts.get(m) ?? 0
      allowance.set(m, take)
      remaining -= take
    }
    open = open.filter(m => !settled.includes(m))
  }
  for (const m of open) if (!allowance.has(m)) allowance.set(m, 0)
  return allowance
}

/**
 * Trim the cache down to `maxQuestions`, the admin-configured
 * pack_questions_limit.
 *
 * `modes` matters once more than one mode is cached. Trimming oldest-first
 * across the whole store would evict whichever mode filled first, so a
 * student who can reach both surfaces could end up with one of them empty
 * while still sitting exactly on the cap. Passing the modes trims each one
 * to its own water-filled allowance instead, oldest-first within that mode.
 *
 * Omitting `modes` keeps the original global oldest-first behaviour, which is
 * correct when only one mode is ever cached.
 */
export async function enforcePackLimit(
  maxQuestions: number,
  modes?: (CachedQuestionMode | null)[],
): Promise<number> {
  if (maxQuestions <= 0) return 0
  try {
    const db = await getDb()
    const all = (await db.getAll('question-cache')) as QuestionEntry[]
    if (all.length <= maxQuestions) return 0

    const oldestFirst = (a: QuestionEntry, b: QuestionEntry) => (
      a.cachedAt - b.cachedAt
      || (a.offlineAttemptedAt ?? 0) - (b.offlineAttemptedAt ?? 0)
      || a.id.localeCompare(b.id)
    )

    let doomed: QuestionEntry[]

    if (modes && modes.length > 1) {
      const byMode = new Map<CachedQuestionMode | null, QuestionEntry[]>()
      for (const m of modes) byMode.set(m, [])
      // Rows whose mode is not in the requested set (stale after a mode
      // change) are evicted first — they serve no reachable surface.
      const orphans: QuestionEntry[] = []
      for (const e of all) {
        const bucket = byMode.get(e.mode)
        if (bucket) bucket.push(e)
        else orphans.push(e)
      }
      const counts = new Map([...byMode].map(([m, rows]) => [m, rows.length] as const))
      const allowance = allocatePackBudget(maxQuestions, counts)

      doomed = [...orphans]
      for (const [m, rows] of byMode) {
        const keep = allowance.get(m) ?? 0
        if (rows.length > keep) {
          doomed.push(...[...rows].sort(oldestFirst).slice(0, rows.length - keep))
        }
      }
    } else {
      doomed = [...all].sort(oldestFirst).slice(0, all.length - maxQuestions)
    }

    if (doomed.length === 0) return 0
    const tx = db.transaction('question-cache', 'readwrite')
    for (const entry of doomed) await tx.store.delete(entry.id)
    await tx.done
    return doomed.length
  } catch (err) {
    console.error('[offlineDb] enforcePackLimit failed', err)
    return 0
  }
}

/**
 * Rebuild pack metadata from what is actually cached.
 *
 * PackMeta is a singleton keyed 'current', so with more than one mode being
 * filled, two writers would each claim the whole pack and the last one would
 * win with a partial count. Deriving it from the store instead is race-free:
 * whichever fill finishes last recomputes the same correct totals, because it
 * reads the union rather than merging its own slice.
 *
 * Subject names come from the cached questions themselves, so no extra
 * plumbing is needed to carry them through the fill.
 */
export async function recomputePackMeta(programmeNames: string[]): Promise<PackMeta | null> {
  try {
    const db = await getDb()
    const all = (await db.getAll('question-cache')) as QuestionEntry[]
    if (all.length === 0) {
      await clearPackMetaOnly()
      return null
    }

    const bySubject = new Map<string, { code: string; name: string; count: number }>()
    for (const e of all) {
      const code = e.subjectCode
      const existing = bySubject.get(code)
      if (existing) existing.count++
      else bySubject.set(code, { code, name: e.question.subject?.name ?? code, count: 1 })
    }

    const meta: PackMeta = {
      id: 'current',
      programmeNames,
      subjects: [...bySubject.values()].sort((a, b) => a.code.localeCompare(b.code)),
      totalQuestions: all.length,
      downloadedAt: new Date().toISOString(),
      creditsDeducted: 0,
    }
    await savePackMeta(meta)
    return meta
  } catch (err) {
    console.error('[offlineDb] recomputePackMeta failed', err)
    return null
  }
}

/** Clear only the metadata, leaving cached questions alone. */
async function clearPackMetaOnly() {
  writeStoredPackMeta(null)
  try {
    const db = await getDb()
    await db.delete('pack-meta', 'current')
  } catch { /* best-effort */ }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(OFFLINE_PACK_META_UPDATED_EVENT))
  }
}

/** Clear the entire pack — removes all cached questions AND pack metadata. */
export async function clearPack() {
  writeStoredPackMeta(null)
  try {
    const db = await getDb()
    const tx = db.transaction(['question-cache', 'pack-meta'], 'readwrite')
    await tx.objectStore('question-cache').clear()
    await tx.objectStore('pack-meta').clear()
    await tx.done
  } catch (err) {
    console.error('[offlineDb] clearPack failed', err)
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(OFFLINE_PACK_META_UPDATED_EVENT))
  }
}

/**
 * Store a full offline pack of questions (all subjects mixed).
 * Clears the question cache first, then inserts all pack questions.
 */
export async function cachePackQuestions(questions: CachedQuestion[], opts: CacheWriteOptions = {}) {
  const db = await getDb()
  const tx = db.transaction('question-cache', 'readwrite')
  await tx.store.clear()
  for (const q of questions) {
    const subjectCode = q.subject?.code ?? 'UNK'
    await tx.store.put(buildQuestionEntry(subjectCode, q, {
      origin: opts.origin ?? 'pack_download',
      mode: opts.mode ?? null,
      cachedAtBand: opts.cachedAtBand ?? null,
    }))
  }
  await tx.done
}

/** Total number of questions in the cache (all subjects). */
export async function getPackQuestionCount(): Promise<number> {
  return withDbRetry(db => db.count('question-cache'))
}

export async function markCachedQuestionAttempted(questionId: string, isCorrect: boolean) {
  const db = await getDb()
  const entry = await db.get('question-cache', questionId) as QuestionEntry | undefined
  if (!entry) return
  await db.put('question-cache', {
    ...entry,
    offlineAttemptedAt: Date.now(),
    offlineWasCorrect: isCorrect,
  } satisfies QuestionEntry)
}

// ── Attempt queue ─────────────────────────────────────────────────────────────

/** Add an attempt to the queue (called when offline). */
export async function enqueueAttempt(attempt: QueuedAttempt) {
  const db = await getDb()
  await db.put('attempt-queue', attempt)
}

/** All queued attempts in insertion order. */
export async function getPendingAttempts(): Promise<QueuedAttempt[]> {
  return withDbRetry(db => db.getAll('attempt-queue') as Promise<QueuedAttempt[]>)
}

/** Remove an attempt after it has been successfully synced. */
export async function deleteQueuedAttempt(id: string) {
  const db = await getDb()
  await db.delete('attempt-queue', id)
}

/** How many attempts are waiting to be synced. */
export async function pendingAttemptCount(): Promise<number> {
  return withDbRetry(db => db.count('attempt-queue'))
}

// ── Exam answer queue ────────────────────────────────────────────────────────

export async function enqueueExamAnswer(answer: QueuedExamAnswer) {
  const db = await getDb()
  await db.put('exam-answer-queue', answer)
}

export async function getPendingExamAnswers(sessionId?: string | null): Promise<QueuedExamAnswer[]> {
  return withDbRetry(async (db) => {
    const rows = sessionId
      ? await db.getAllFromIndex('exam-answer-queue', 'by-session', sessionId) as QueuedExamAnswer[]
      : await db.getAll('exam-answer-queue') as QueuedExamAnswer[]
    return rows.sort((a, b) => a.queuedAt - b.queuedAt)
  })
}

export async function deleteQueuedExamAnswer(id: string) {
  const db = await getDb()
  await db.delete('exam-answer-queue', id)
}

export async function pendingExamAnswerCount(sessionId?: string | null): Promise<number> {
  if (!sessionId) return withDbRetry(db => db.count('exam-answer-queue'))
  const rows = await getPendingExamAnswers(sessionId)
  return rows.length
}

// ── Figure cache (Phase 3, 2026-06-21) ───────────────────────────────────────
//
// MAT/FIS/QUI questions frequently reference a figure (geometry,
// circuit, molecule). Offline play needs the image bytes locally —
// network URLs are useless when the student is on the train. This
// cache stores blobs keyed by figure URL, with LRU eviction at a hard
// 10MB cap. Server-side WebP variants (compressing band-default PNG
// to a fraction of the size) are a follow-up; today we cache whatever
// bytes the network serves.
//
// The cache is opportunistic — figures are fetched on first render
// when online, not proactively prefetched. Acceptable trade-off
// because (a) the typical student practices online first then goes
// offline, naturally warming the cache; (b) the 10MB cap means
// prefetch would mostly just churn LRU under realistic usage.

interface FigureEntry {
  url: string
  blob: Blob
  bytes: number
  cachedAt: number
  lastAccessedAt: number
}

const FIGURE_CACHE_MAX_BYTES = 10 * 1024 * 1024  // 10 MB hard cap

export async function getCachedFigure(url: string): Promise<Blob | null> {
  const db = await getDb()
  const entry = (await db.get('figure-cache', url)) as FigureEntry | undefined
  if (!entry) return null
  // Touch lastAccessedAt for LRU. Best-effort — failure here just
  // means a slightly less accurate eviction order, not a wrong read.
  try {
    await db.put('figure-cache', { ...entry, lastAccessedAt: Date.now() })
  } catch { /* ignore */ }
  return entry.blob
}

/**
 * Store a figure blob in the cache. Enforces the 10MB cap by evicting
 * oldest-accessed entries until there's room. Returns true on success,
 * false if the blob alone exceeds the cap (in which case the caller
 * should fall back to streaming from the network without caching).
 *
 * Race-safe-enough: two concurrent puts of the same URL collapse via
 * the keyPath upsert, and the eviction loop reads its working set
 * fresh on each call. Worst case under contention is brief temporary
 * over-cap that the next call resolves.
 */
export async function cacheFigure(url: string, blob: Blob): Promise<boolean> {
  if (blob.size >= FIGURE_CACHE_MAX_BYTES) return false  // single image bigger than cap
  const db = await getDb()
  const now = Date.now()

  // Compute current total bytes minus this URL's existing entry (so
  // re-caching the same URL is a no-op size-wise).
  const all = (await db.getAll('figure-cache')) as FigureEntry[]
  let currentBytes = all.reduce((sum, e) => sum + e.bytes, 0)
  const existing = all.find(e => e.url === url)
  if (existing) currentBytes -= existing.bytes

  // Evict LRU until the new blob fits.
  if (currentBytes + blob.size > FIGURE_CACHE_MAX_BYTES) {
    const evictionPool = all
      .filter(e => e.url !== url)
      .sort((a, b) => a.lastAccessedAt - b.lastAccessedAt)
    for (const victim of evictionPool) {
      if (currentBytes + blob.size <= FIGURE_CACHE_MAX_BYTES) break
      try {
        await db.delete('figure-cache', victim.url)
        currentBytes -= victim.bytes
      } catch { /* skip, try next */ }
    }
  }

  const entry: FigureEntry = { url, blob, bytes: blob.size, cachedAt: now, lastAccessedAt: now }
  await db.put('figure-cache', entry)
  return true
}

/** Sum of cached figure bytes. Surfaces in /profile offline status. */
export async function figureCacheBytes(): Promise<number> {
  const db = await getDb()
  const all = (await db.getAll('figure-cache')) as FigureEntry[]
  return all.reduce((sum, e) => sum + e.bytes, 0)
}
